import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ClipboardCheck,
  FileDown,
  Pencil,
  Ruler,
  ShieldCheck,
  User as UserIcon,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { listUsers } from "@/lib/api/users";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { StatusBadge } from "@/components/StatusBadge";
import { GenerateFormDialog } from "@/features/verdict/GenerateFormDialog";
import { SpecialtyExtraFields } from "@/features/examinations/SpecialtyExtraFields";
import { VerdictForm } from "@/features/verdict/VerdictForm";
import {
  addSectionToExam,
  type DoctorSpecialty,
  getExamination,
  type IntakeVitals,
  updateExamination,
} from "@/lib/api/examinations";
import { getPatient } from "@/lib/api/patients";
import { formatDate, formatDateTime } from "@/lib/format";
import { useAuthStore } from "@/stores/auth";

export function ExaminationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canIssueVerdict = useAuthStore((s) => s.hasPermission("final_verdict.issue"));
  const canGenerateForm = useAuthStore((s) => s.hasPermission("form.generate"));
  const canAddSection = useAuthStore((s) => s.hasPermission("examination.update"));
  const currentUser = useAuthStore((s) => s.user);
  const [formOpen, formHandlers] = useDisclosure(false);
  const [addOpen, addHandlers] = useDisclosure(false);
  const [newSpecialty, setNewSpecialty] = useState<DoctorSpecialty | "">("");

  const addSectionMutation = useMutation({
    mutationFn: (specialty: DoctorSpecialty) => addSectionToExam(id!, specialty),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["examination", id] });
      notifications.show({ color: "green", message: t("examinations.section_added") });
      addHandlers.close();
      setNewSpecialty("");
    },
    onError: () => {
      notifications.show({ color: "red", message: t("examinations.section_add_failed") });
    },
  });

  const { data: exam, isLoading } = useQuery({
    queryKey: ["examination", id],
    queryFn: () => getExamination(id!),
    enabled: !!id,
  });

  const { data: patient } = useQuery({
    queryKey: ["patient", exam?.patient_id],
    queryFn: () => getPatient(exam!.patient_id),
    enabled: !!exam?.patient_id,
  });

  const { data: usersList } = useQuery({
    queryKey: ["users-all"],
    queryFn: () => listUsers({ limit: 200 }),
    staleTime: 60_000,
  });
  const userById = new Map(
    (usersList?.items ?? []).map((u) => [u.id, u.full_name]),
  );

  const form = useForm<IntakeVitals>({
    initialValues: {
      height_cm: null,
      weight_kg: null,
      systolic_bp: null,
      diastolic_bp: null,
      pulse_bpm: null,
      temperature_c: null,
    },
  });

  useEffect(() => {
    if (exam?.intake_vitals) {
      form.setValues(exam.intake_vitals);
    }
  }, [exam?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateMutation = useMutation({
    mutationFn: (vitals: IntakeVitals) => updateExamination(id!, { intake_vitals: vitals }),
    onSuccess: () => {
      notifications.show({ color: "blue", message: t("examinations.intake_saved") });
      queryClient.invalidateQueries({ queryKey: ["examination", id] });
    },
  });

  if (isLoading || !exam) return <Loader />;

  const bmi = (() => {
    const h = form.values.height_cm;
    const w = form.values.weight_kg;
    if (!h || !w) return null;
    const m = h / 100;
    return (w / (m * m)).toFixed(1);
  })();

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Group gap="md">
          <UnstyledButton
            onClick={() => navigate(-1)}
            style={{ color: "var(--mantine-color-gray-6)", padding: 8, borderRadius: 999 }}
          >
            <ArrowLeft size={22} strokeWidth={2} />
          </UnstyledButton>
          <Stack gap={2}>
            <Title order={3} fw={700}>
              {t("examinations.detail_title")}
            </Title>
            <Group gap={6}>
              <StatusBadge status={exam.status} i18nPrefix="examinations.statuses" />
              <Badge variant="light" color="blue" radius="sm">
                {t(`examinations.types.${exam.type}`)}
              </Badge>
            </Group>
          </Stack>
        </Group>

        <Group gap="md">
          {canGenerateForm && exam.final_verdict !== null && (
            <Button
              leftSection={<FileDown size={16} strokeWidth={2.2} />}
              radius="xl"
              onClick={formHandlers.open}
              style={{ boxShadow: "0 6px 16px rgba(37, 99, 235, 0.25)" }}
            >
              {t("verdict.generate")}
            </Button>
          )}
        </Group>
      </Group>

      {patient && (
        <Card padding="lg" radius="xl">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Group gap="md" wrap="nowrap">
              <ThemeIcon
                size={48}
                radius="xl"
                variant="gradient"
                gradient={{ from: "brand.5", to: "brand.8", deg: 135 }}
              >
                <UserIcon size={20} strokeWidth={2.2} color="white" />
              </ThemeIcon>
              <Stack gap={2}>
                <Group gap="sm">
                  <Text fw={700} size="lg" component={Link} to={`/patients/${patient.id}`}>
                    {patient.family_name} {patient.given_name}
                  </Text>
                  <Badge variant="light" color="gray" radius="sm" size="sm">
                    {patient.card_number}
                  </Badge>
                </Group>
                <Text c="dimmed" size="sm">
                  {patient.birth_date} · {t("patients.years_old", { age: patient.age })}
                </Text>
                {exam.job_title && (
                  <Group gap={6} mt={2}>
                    <Text size="xs" c="brand.7" fw={700} tt="uppercase">
                      {t("examinations.fields.job_title")}:
                    </Text>
                    <Text size="sm" fw={600}>
                      {exam.job_title}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Group>
          </Group>
        </Card>
      )}

      {currentUser?.role === "medical_commission" ? (
        <Card padding="xl" radius="xl">
          <Group gap="sm" mb="md">
            <ThemeIcon size={36} radius="xl" variant="light" color="brand">
              <ClipboardCheck size={18} strokeWidth={2} />
            </ThemeIcon>
            <Title order={5} fw={700}>
              {t("examinations.commission_summary")}
            </Title>
          </Group>
          <Stack gap="md">
            {exam.sections.length === 0 && (
              <Text c="dimmed" size="sm">{t("examinations.no_sections_yet")}</Text>
            )}
            {exam.sections.map((s) => (
              <Card key={s.id} padding="md" radius="lg" withBorder>
                <Group justify="space-between" mb="xs" wrap="nowrap">
                  <Stack gap={2}>
                    <Text fw={700} size="sm" c="brand">
                      {t(`users.specialties.${s.specialty}`)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {s.completed_user_name
                        ?? s.assigned_user_name
                        ?? t("examinations.doctor_pending")}
                    </Text>
                  </Stack>
                  <Group gap="xs">
                    {s.completed_at ? (
                      <StatusBadge status="completed" i18nPrefix="examinations.statuses" />
                    ) : (
                      <StatusBadge status="pending" i18nPrefix="examinations.statuses" />
                    )}
                    {s.verdict && (
                      <StatusBadge status={s.verdict} i18nPrefix="sections.verdicts" />
                    )}
                  </Group>
                </Group>

                {s.complaints && (
                  <Text size="xs" mt={4}>
                    <Text component="span" fw={700} c="dimmed">
                      {t("sections.fields.complaints")}:
                    </Text>{" "}
                    {s.complaints}
                  </Text>
                )}
                {s.objective_findings && (
                  <Text size="xs" mt={4}>
                    <Text component="span" fw={700} c="dimmed">
                      {t("sections.fields.objective_findings")}:
                    </Text>{" "}
                    {s.objective_findings}
                  </Text>
                )}
                {(s.diagnosis_code || s.diagnosis_text) && (
                  <Text size="sm" mt={4}>
                    <Text component="span" fw={700} c="dimmed" size="xs">
                      {t("sections.fields.diagnosis_code")}:
                    </Text>{" "}
                    {s.diagnosis_code && (
                      <Text component="span" fw={700} ff="monospace" c="brand">
                        {s.diagnosis_code}
                      </Text>
                    )}
                    {s.diagnosis_text ? ` — ${s.diagnosis_text}` : ""}
                  </Text>
                )}
                {s.recommendations && (
                  <Text size="xs" mt={4}>
                    <Text component="span" fw={700} c="dimmed">
                      {t("sections.fields.recommendations")}:
                    </Text>{" "}
                    {s.recommendations}
                  </Text>
                )}

                {/* Specialty-specific tekshiruv (e.g. dermatovenerolog,
                    oftalmolog, ent...) — rendered read-only via the same
                    component used in edit mode. */}
                {s.specialty !== "laboratory" && s.extra_fields &&
                  Object.keys(s.extra_fields).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <SpecialtyExtraFields
                        specialty={s.specialty}
                        values={s.extra_fields}
                        onChange={() => {}}
                        disabled
                      />
                    </div>
                  )}

                {/* Laboratory: show the filled-in template table */}
                {s.lab_template_table && (
                  <Card mt="sm" padding="xs" radius="md" bg="gray.0">
                    <Text size="xs" fw={700} c="dimmed" mb={6}>
                      {s.lab_template_table.name}
                    </Text>
                    <Table withTableBorder withColumnBorders striped fz="xs">
                      <Table.Thead>
                        <Table.Tr>
                          {s.lab_template_table.headers.map((h, i) => (
                            <Table.Th key={i}>{h}</Table.Th>
                          ))}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {s.lab_template_table.rows.map((row, i) => (
                          <Table.Tr key={i}>
                            {row.map((cell, j) => (
                              <Table.Td key={j} style={{ whiteSpace: "pre-line" }}>
                                {cell}
                              </Table.Td>
                            ))}
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Card>
                )}
              </Card>
            ))}
          </Stack>
        </Card>
      ) : (
      <Card padding={0} radius="xl">
        <Group justify="space-between" p="lg" pb="md">
          <Group gap="sm">
            <ThemeIcon size={36} radius="xl" variant="light" color="brand">
              <ClipboardCheck size={18} strokeWidth={2} />
            </ThemeIcon>
            <Title order={5} fw={700}>
              {t("examinations.section.sections")}
            </Title>
          </Group>
          {canAddSection && (
            <Button
              size="sm"
              radius="xl"
              variant="light"
              color="brand"
              leftSection={<Pencil size={14} strokeWidth={2.2} />}
              onClick={addHandlers.open}
            >
              {t("examinations.add_specialist")}
            </Button>
          )}
        </Group>

        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("examinations.fields.specialty")}</Table.Th>
                <Table.Th>{t("examinations.fields.doctor")}</Table.Th>
                <Table.Th>{t("examinations.fields.section_status")}</Table.Th>
                <Table.Th>{t("examinations.fields.diagnosis")}</Table.Th>
                <Table.Th>{t("examinations.fields.section_verdict")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(() => {
                const role = currentUser?.role;
                const userSpecialty = currentUser?.specialty;
                const seesAll =
                  role === "admin" ||
                  role === "chief_doctor" ||
                  role === "registrar";
                const visibleSections = seesAll
                  ? exam.sections
                  : exam.sections.filter((sec) => {
                      if (role === "lab_tech") return sec.specialty === "laboratory";
                      if (role === "doctor")
                        return userSpecialty && sec.specialty === userSpecialty;
                      return false;
                    });
                return visibleSections;
              })().map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td>
                    <Text fw={600} size="sm">
                      {t(`users.specialties.${s.specialty}`)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={s.completed_user_id || s.assigned_user_id ? undefined : "dimmed"}>
                      {s.completed_user_name
                        ?? s.assigned_user_name
                        ?? (s.completed_user_id
                          ? userById.get(s.completed_user_id) ?? "—"
                          : s.assigned_user_id
                            ? userById.get(s.assigned_user_id) ?? "—"
                            : t("examinations.doctor_pending"))}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {s.completed_at ? (
                      <StatusBadge status="completed" i18nPrefix="examinations.statuses" />
                    ) : (
                      <StatusBadge status="pending" i18nPrefix="examinations.statuses" />
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {s.diagnosis_code ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {s.verdict ? (
                      <StatusBadge status={s.verdict} i18nPrefix="sections.verdicts" />
                    ) : (
                      "—"
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      radius="xl"
                      component={Link}
                      to={`/examinations/${exam.id}/sections/${s.specialty}`}
                      rightSection={<ArrowRight size={14} strokeWidth={2.2} />}
                      leftSection={<Pencil size={14} strokeWidth={2.2} />}
                    >
                      {t("sections.open")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
      )}

      {id && canIssueVerdict && exam.final_verdict === null && exam.status === "pending_chief" && (
        <VerdictForm examId={id} />
      )}

      {exam.final_verdict && (
        <Card
          padding="xl"
          radius="xl"
          style={{
            background: "var(--mantine-color-brand-0)",
            borderColor: "var(--mantine-color-brand-3)",
          }}
        >
          <Group gap="sm" mb="lg">
            <ThemeIcon size={36} radius="xl" variant="filled" color="brand.7">
              <ShieldCheck size={18} strokeWidth={2.4} color="white" />
            </ThemeIcon>
            <Stack gap={0}>
              <Title order={5} fw={700} c="brand.9">
                {t("verdict.profpat_title")}
              </Title>
              {exam.chief_signed_at && (
                <Text size="xs" c="dimmed">
                  {t("verdict.signed_at")}: {formatDateTime(exam.chief_signed_at)}
                  {exam.chief_signed_user_id
                    ? ` · ${userById.get(exam.chief_signed_user_id) ?? ""}`
                    : ""}
                </Text>
              )}
            </Stack>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            {exam.verdict_diagnosis_summary && (
              <Card withBorder padding="md" radius="md">
                <Text size="xs" fw={700} tt="uppercase" c="brand.7" mb={4}>
                  {t("verdict.profpat.diagnosis")}
                </Text>
                <Text size="sm" fw={600}>
                  {exam.verdict_diagnosis_summary}
                </Text>
              </Card>
            )}
            {exam.verdict_profession_fitness && (
              <Card withBorder padding="md" radius="md">
                <Text size="xs" fw={700} tt="uppercase" c="brand.7" mb={4}>
                  {t("verdict.profpat.profession_fitness")}
                </Text>
                <Badge
                  color={exam.verdict_profession_fitness === "fit" ? "brand" : "red"}
                  variant="light"
                  size="lg"
                  radius="sm"
                >
                  {exam.verdict_profession_fitness === "fit"
                    ? t("verdict.profpat.fit_in_profession")
                    : t("verdict.profpat.unfit_in_profession")}
                </Badge>
              </Card>
            )}
            {exam.verdict_pregnancy_relevant !== null && (
              <Card withBorder padding="md" radius="md">
                <Text size="xs" fw={700} tt="uppercase" c="brand.7" mb={4}>
                  {t("verdict.profpat.pregnancy")}
                </Text>
                <Text size="sm" fw={600}>
                  {exam.verdict_pregnancy_relevant ? t("common.yes") : t("common.no")}
                </Text>
              </Card>
            )}
            {exam.verdict_referral_kind && (
              <Card withBorder padding="md" radius="md">
                <Text size="xs" fw={700} tt="uppercase" c="brand.7" mb={4}>
                  {t("verdict.profpat.referral")}
                </Text>
                <Text size="sm" fw={600}>
                  {t(`verdict.profpat.referral_${exam.verdict_referral_kind}`, {
                    defaultValue: exam.verdict_referral_kind,
                  })}
                </Text>
                {exam.verdict_referral_notes && (
                  <Text size="xs" c="dimmed" mt={4}>
                    {exam.verdict_referral_notes}
                  </Text>
                )}
              </Card>
            )}
            <Card
              withBorder
              padding="md"
              radius="md"
              style={{ gridColumn: "1 / -1", borderColor: "var(--mantine-color-brand-5)" }}
            >
              <Text size="xs" fw={700} tt="uppercase" c="brand.7" mb={4}>
                {t("verdict.profpat.final_decision")}
              </Text>
              <Badge
                size="xl"
                color={exam.verdict_final_decision === "fit" ? "brand" : "red"}
                variant="filled"
                radius="sm"
              >
                {exam.verdict_final_decision === "fit"
                  ? t("verdict.profpat.final_fit")
                  : exam.verdict_final_decision === "unfit"
                    ? t("verdict.profpat.final_unfit")
                    : "—"}
              </Badge>
              {exam.final_verdict_notes && (
                <Text size="sm" mt="md">
                  {exam.final_verdict_notes}
                </Text>
              )}
              {exam.valid_until && (
                <Text size="xs" c="dimmed" mt="xs">
                  {t("verdict.fields.valid_until")}: {exam.valid_until}
                </Text>
              )}
            </Card>
          </SimpleGrid>
        </Card>
      )}

      {id && (
        <GenerateFormDialog examId={id} opened={formOpen} onClose={formHandlers.close} />
      )}

      <Modal
        opened={addOpen}
        onClose={addHandlers.close}
        title={t("examinations.add_specialist_title")}
        size="md"
        radius="lg"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {t("examinations.add_specialist_hint")}
          </Text>
          {(() => {
            const ALL: DoctorSpecialty[] = [
              "therapist", "neurologist", "ophthalmologist", "ent",
              "surgeon", "gynecologist", "urologist", "dentist",
              "psychiatrist_narcologist", "dermatovenerologist",
              "cardiologist", "radiologist", "laboratory",
            ];
            const existing = new Set((exam.sections ?? []).map((s) => s.specialty));
            // Sex-based filtering: gynecologist for female only, urologist for male only
            const sex = patient?.sex;
            const sexHidden = new Set<DoctorSpecialty>(
              sex === "male"
                ? ["gynecologist"]
                : sex === "female"
                  ? ["urologist"]
                  : [],
            );
            const visible = ALL.filter((s) => !sexHidden.has(s));
            const available = visible.filter((s) => !existing.has(s));
            if (available.length === 0) {
              return (
                <Text size="sm" c="dimmed" ta="center" py="md">
                  {t("examinations.add_specialist_none")}
                </Text>
              );
            }
            // Show ALL visible specialties — already-attached ones appear disabled
            // so the user sees the full list and understands what's done.
            const data = visible.map((s) => ({
              value: s,
              label: existing.has(s)
                ? `${t(`users.specialties.${s}`)} ✓`
                : t(`users.specialties.${s}`),
              disabled: existing.has(s),
            }));
            return (
              <Select
                label={t("examinations.fields.specialty")}
                placeholder={t("placeholder.select_specialist")}
                data={data}
                value={newSpecialty}
                onChange={(v) => setNewSpecialty((v as DoctorSpecialty) ?? "")}
                searchable
              />
            );
          })()}
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={addHandlers.close}>
              {t("common.cancel")}
            </Button>
            <Button
              color="brand"
              loading={addSectionMutation.isPending}
              disabled={!newSpecialty}
              onClick={() => newSpecialty && addSectionMutation.mutate(newSpecialty)}
            >
              {t("common.create")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
